import api from './api';
import type { RunwayCalendarData } from '@/utils/planningRunway';

/**
 * Team API Service
 *
 * Handles all API calls related to team management:
 * - Fetching team details and members
 * - Updating team name
 * - Managing member roles
 * - Removing members
 */

export interface TeamMember {
  id: number;
  email: string;
  fullName: string;
  teamRole: string | null;
  createdAt: string;
  preferences?: {
    avatar?: string;
  } | null;
}

// Alias for backwards compatibility in components
export type { TeamMember as TeamMemberWithRole };

export interface TeamStats {
  memberCount: number;
  channelCount: number;
  postCount: number;
}

export interface Team {
  id: number;
  name: string;
  timezone: string;
  postTargetWeekly: number | null;
  lifecycleStage: string | null;
  canManageBrand: boolean;
  createdAt: string;
  members: TeamMember[];
  stats: TeamStats;
  agencyId?: number | null; // If set, this is an agency client workspace
}

export const teamApi = {
  getPlanningRunway: async (): Promise<{ todayKey: string; timezone: string; calendar: RunwayCalendarData }> => {
    const response = await api.get('/api/team/planning-runway');
    return response.data.data;
  },
  /**
   * Get team details with members
   */
  getTeam: async (): Promise<Team> => {
    const response = await api.get('/api/team');
    return response.data.data;
  },

  /**
   * Update team settings (admin only)
   */
  updateTeam: async (data: { name?: string; timezone?: string; postTargetWeekly?: number }): Promise<{ id: number; name: string; timezone: string; postTargetWeekly: number | null }> => {
    const response = await api.patch('/api/team', data);
    return response.data.data;
  },

  /**
   * Update a member's role (admin only)
   */
  updateMemberRole: async (memberId: number, role: string): Promise<TeamMember> => {
    const response = await api.patch(`/api/team/members/${memberId}/role`, { role });
    return response.data.data;
  },

  /**
   * Remove a member from team (admin only)
   */
  removeMember: async (memberId: number): Promise<void> => {
    await api.delete(`/api/team/members/${memberId}`);
  },
};

export default teamApi;
